import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  ChevronRight, Loader2, MapPin, Briefcase,
  Scissors, Camera, GraduationCap, Stethoscope, Home, Brain,
  ShoppingBag, Sparkles, Hammer, Star, Zap, Droplets,
  Flower, HeartPulse, Palette, Code, Calculator, Wrench,
  Scale, Utensils, Car, PenTool, Monitor, Hand, TreePine,
  Dumbbell, KeyRound, PaintBucket, Apple, Ruler, FileText,
  Pill, Building, Shield, Sun, Thermometer, Truck, Circle,
  Baby, Globe, Languages, Shirt, Cake, PartyPopper, Music,
  Video, Printer, Megaphone, Heart, Grid3x3, DoorOpen, Fence, Search
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import ApisAIFooter from '@/components/ApisAIFooter';
import CatalogConcierge from '@/components/CatalogConcierge';
import { BreadcrumbSchema } from '@/components/BreadcrumbSchema';
import { API } from '@/utils/api';

const ICON_MAP = {
  'scissors': Scissors, 'camera': Camera, 'graduation-cap': GraduationCap,
  'stethoscope': Stethoscope, 'home': Home, 'brain': Brain,
  'shopping-bag': ShoppingBag, 'sparkles': Sparkles, 'hammer': Hammer,
  'star': Star, 'zap': Zap, 'droplets': Droplets, 'flower': Flower,
  'heart-pulse': HeartPulse, 'palette': Palette, 'code': Code,
  'calculator': Calculator, 'wrench': Wrench, 'scale': Scale,
  'utensils': Utensils, 'car': Car, 'pen-tool': PenTool,
  'monitor': Monitor, 'hand': Hand, 'tree-pine': TreePine,
  'dumbbell': Dumbbell, 'key-round': KeyRound, 'paint-bucket': PaintBucket,
  'apple': Apple, 'ruler': Ruler, 'file-text': FileText,
  'pill': Pill, 'building': Building, 'shield': Shield,
  'sun': Sun, 'thermometer': Thermometer, 'truck': Truck,
  'circle': Circle, 'baby': Baby, 'globe': Globe,
  'languages': Languages, 'shirt': Shirt, 'cake': Cake,
  'party-popper': PartyPopper, 'music': Music, 'video': Video,
  'printer': Printer, 'megaphone': Megaphone, 'heart': Heart,
  'map-pin': MapPin, 'grid': Grid3x3, 'door-open': DoorOpen,
  'fence': Fence, 'search': Search,
};

export default function BusinessCatalogCity() {
  const { city } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/public/katalog-miasto/${city}`).then(res => setData(res.data)).catch(console.error).finally(() => setLoading(false));
  }, [city]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  const { city: cityName, categories = [], total = 0, faq = [], seo = {} } = data || {};

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={`https://apisai.pl${seo.canonical}`} />
        {seo.schema && <script type="application/ld+json">{JSON.stringify(seo.schema)}</script>}
        {seo.faq_schema && <script type="application/ld+json">{JSON.stringify(seo.faq_schema)}</script>}
        {seo.og && (
          <>
            <meta property="og:title" content={seo.og.title} />
            <meta property="og:description" content={seo.og.description} />
            <meta property="og:type" content={seo.og.type} />
            <meta property="og:url" content={seo.og.url} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={seo.og.title} />
            <meta name="twitter:description" content={seo.og.description} />
          </>
        )}
      </Helmet>
      <BreadcrumbSchema breadcrumbs={seo.breadcrumbs} />

      <div className="min-h-screen bg-[#0B0F1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0B0F1A]/80 backdrop-blur-xl border-b border-white/5">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.svg" alt="ApisAI" className="w-9 h-9" />
              <span className="font-semibold text-lg text-white">ApisAI</span>
            </Link>
            <Link to="/rejestracja">
              <Button className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 rounded-full px-6 text-sm">
                Dodaj swoją firmę
              </Button>
            </Link>
          </div>
        </nav>

        <div className="pt-24 pb-4">
          <div className="max-w-5xl mx-auto px-6">
            <div className="flex items-center gap-2 text-sm text-gray-400 flex-wrap" data-testid="breadcrumbs">
              {seo.breadcrumbs?.map((b, i) => (
                <span key={i} className="flex items-center gap-2">
                  {i > 0 && <ChevronRight className="w-4 h-4" />}
                  {i === seo.breadcrumbs.length - 1 ? (
                    <span className="text-white">{b.name}</span>
                  ) : (
                    <Link to={b.url} className="hover:text-white transition-colors">{b.name}</Link>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>

        <section className="pb-12">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-4">
                <MapPin className="w-4 h-4" />
                {cityName}
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3" data-testid="page-title">
                {seo.h1}
              </h1>
              <p className="text-lg text-gray-400">{total} firm w katalogu</p>
            </motion.div>

            {/* AI Koncierż — tryb „firmy" (zablokowany, bez przełącznika) */}
            <div className="mb-10" data-testid="business-concierge">
              <CatalogConcierge lockMode="firmy" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="city-categories">
              {categories.map((cat, i) => {
                const IconComp = ICON_MAP[cat.icon] || Briefcase;
                return (
                  <motion.div key={cat.slug} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}>
                    <Link
                      to={`/katalog/${cat.slug}/${city}`}
                      className="block p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-all group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-500/20 transition-colors">
                          <IconComp className="w-4 h-4 text-emerald-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-white text-sm truncate group-hover:text-emerald-400 transition-colors">{cat.plural}</p>
                          <p className="text-xs text-gray-500">{cat.count} firm</p>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>

            {faq?.length > 0 && (
              <div className="mt-12" data-testid="faq-section">
                <h2 className="text-xl font-bold text-white mb-6">Najczęściej zadawane pytania</h2>
                <div className="space-y-3">
                  {faq.map((item, i) => (
                    <details key={i} className="group rounded-xl bg-white/[0.03] border border-white/[0.06] overflow-hidden">
                      <summary className="flex items-center justify-between p-5 cursor-pointer text-white font-medium text-sm hover:bg-white/[0.02] transition-colors">
                        {item.q}
                        <ChevronRight className="w-4 h-4 text-gray-500 group-open:rotate-90 transition-transform flex-shrink-0 ml-4" />
                      </summary>
                      <div className="px-5 pb-5 text-gray-400 text-sm leading-relaxed border-t border-white/5 pt-4">
                        {item.a}
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-12">
              <h2 className="text-lg font-bold text-white mb-4">Powiązane</h2>
              <div className="flex flex-wrap gap-2">
                <Link to="/katalog" className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white transition-colors text-sm">
                  Wszystkie kategorie
                </Link>
                <Link to="/rasy" className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white transition-colors text-sm">
                  Katalog ras psów
                </Link>
              </div>
            </div>

            <div className="mt-10 text-center p-8 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-blue-500/10 border border-emerald-500/20">
              <h2 className="text-xl font-bold text-white mb-2">Prowadzisz firmę w {cityName}?</h2>
              <p className="text-gray-400 mb-5">Dodaj się do katalogu i docieraj do klientów w swoim mieście.</p>
              <Link to="/rejestracja">
                <Button className="bg-emerald-500 hover:bg-emerald-600 rounded-full px-8">
                  Dodaj firmę za darmo
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <ApisAIFooter variant="light" pageType="catalog" pageSlug={`miasto-${city}`} />
      </div>
    </>
  );
}
